// src/hooks/useContractDocumentUpload.ts
// Reusable manual document-upload flow for a contract's solicitation documents.
// Lifted from SolicitationDocumentsStep so the same client-side extract -> storage
// -> DB -> server-extract-fallback -> RAG-ingest pipeline is available anywhere a
// user needs to attach files (e.g. the contract slideout), not just mid-generation.
//
// This is what lets a user add documents that aren't downloadable from SAM.gov —
// e.g. DoD solicitations whose real files (draft PWS, price schedule) live in PIEE
// behind authenticated access and therefore can never be auto-fetched.

import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { DocumentAnalyzer } from '../lib/document-analysis';
import toast from 'react-hot-toast';

export interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'extracting' | 'processing' | 'completed' | 'failed';
  error?: string;
  wordCount?: number;
}

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'];

function withTimeout<T>(promise: Promise<T>, ms: number, name: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Text extraction timed out for ${name}`)), ms)
    ),
  ]);
}

async function extractTextClientSide(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const isPdf = file.type === 'application/pdf' || ext === 'pdf';
  const maxMs = isPdf ? 120000 : 60000;
  const run = (async () => {
    if (file.type === 'text/plain' || file.type === 'text/csv' || ext === 'txt' || ext === 'csv') {
      return await file.text();
    }
    return await DocumentAnalyzer.processFile(file);
  })();
  return await withTimeout(run, maxMs, file.name);
}

/**
 * Manual document upload for a contract. Returns the in-flight upload state and a
 * handler that accepts a FileList/File[]. On success it refreshes (via onComplete)
 * and triggers RAG ingestion so the new docs are immediately usable by analysis +
 * proposal generation.
 */
export function useContractDocumentUpload(
  noticeId: string,
  onComplete?: () => void | Promise<void>
) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);

  const processFile = useCallback(
    async (
      file: File,
      fileIndex: number,
      startIndex: number,
      update: (i: number, u: Partial<UploadingFile>) => void
    ): Promise<void> => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const i = startIndex + fileIndex;
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) throw new Error('Not authenticated');

        // Duplicate guard
        const { data: existing } = await supabase
          .from('contract_documents')
          .select('id, status')
          .eq('contract_id', noticeId)
          .eq('document_name', file.name)
          .maybeSingle();
        if (existing) throw new Error(`Already uploaded (${existing.status === 'completed' ? 'ready' : existing.status})`);

        update(i, { status: 'extracting', progress: 10 });
        let extractedText = '';
        let pageCount = 1;
        try {
          extractedText = await extractTextClientSide(file);
          pageCount = Math.max(1, Math.ceil(extractedText.length / 3000));
        } catch (e) {
          console.warn(`Client extraction failed for ${file.name}, saving without text:`, e);
        }
        const wordCount = extractedText.split(/\s+/).filter(Boolean).length;
        update(i, { progress: 40, wordCount });

        update(i, { status: 'uploading', progress: 50 });
        const storedName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
        const filePath = `solicitation-documents/${noticeId}/${storedName}`;
        const { error: upErr } = await supabase.storage.from('documents').upload(filePath, file);
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('documents').getPublicUrl(filePath);

        update(i, { status: 'processing', progress: 80 });
        const hasText = !!extractedText && extractedText.length > 50;
        const { data: inserted, error: dbErr } = await supabase
          .from('contract_documents')
          .insert({
            contract_id: noticeId,
            document_name: file.name,
            document_url: urlData.publicUrl,
            document_type: 'attachment',
            file_type: ext,
            file_size: file.size,
            raw_text: extractedText || null,
            page_count: pageCount,
            status: hasText ? 'completed' : 'pending',
            processed_at: hasText ? new Date().toISOString() : null,
          })
          .select('id')
          .single();
        if (dbErr) {
          if (dbErr.code === '23505') throw new Error('Document already exists');
          throw dbErr;
        }

        // Server-side extraction fallback when the client couldn't read the file.
        if (!hasText && inserted?.id) {
          update(i, { status: 'extracting', progress: 85 });
          try {
            await supabase.functions.invoke('extract-document-text', { body: { documentId: inserted.id } });
          } catch (e) {
            console.warn(`Server extraction trigger failed for ${file.name}:`, e);
          }
        }

        update(i, { status: 'completed', progress: 100 });
      } catch (error) {
        update(i, { status: 'failed', error: (error as Error).message });
        throw error;
      }
    },
    [noticeId]
  );

  const handleFileUpload = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files);
      const valid = arr.filter((f) => {
        const ext = f.name.split('.').pop()?.toLowerCase() || '';
        if (!ALLOWED_TYPES.includes(f.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
          toast.error(`${f.name}: Unsupported file type`);
          return false;
        }
        if (f.size > 50 * 1024 * 1024) {
          toast.error(`${f.name}: File too large (max 50MB)`);
          return false;
        }
        return true;
      });
      if (valid.length === 0) return;

      const startIndex = uploadingFiles.length;
      setUploadingFiles((prev) => [
        ...prev,
        ...valid.map((file) => ({ file, progress: 0, status: 'extracting' as const })),
      ]);
      const update = (idx: number, u: Partial<UploadingFile>) =>
        setUploadingFiles((prev) => prev.map((f, k) => (k === idx ? { ...f, ...u } : f)));

      const results = await Promise.allSettled(
        valid.map((file, idx) => processFile(file, idx, startIndex, update))
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (succeeded > 0) toast.success(`${succeeded} document${succeeded !== 1 ? 's' : ''} uploaded`);
      if (failed > 0) toast.error(`${failed} document${failed !== 1 ? 's' : ''} failed`);

      await onComplete?.();

      // Index the new docs for RAG so analysis + generation can use them right away.
      if (succeeded > 0) {
        try {
          await supabase.functions.invoke('ingest-contract-documents', { body: { contract_id: noticeId } });
        } catch (e) {
          console.warn('RAG ingestion trigger failed (non-blocking):', e);
        }
      }

      setTimeout(() => setUploadingFiles((prev) => prev.filter((f) => f.status !== 'completed')), 2500);
    },
    [noticeId, uploadingFiles.length, processFile, onComplete]
  );

  return { uploadingFiles, handleFileUpload };
}
